import { z } from 'zod'

/**
 * Frontend copy of `SavedViewName` validation. The backend
 * (`@buffer-mono/entities`) owns the canonical schema; this file is a
 * structural duplicate so `publish-frontend` doesn't import a backend
 * package. Any change to the entity schema must be mirrored here.
 */

const MAX_SAVED_VIEW_NAME_LENGTH = 100

// Invisible characters that can be used to spoof or obscure names: zero-width
// space/joiner/non-joiner, BOM/ZWNBSP, and right-to-left override. Uses an
// alternation (not a character class) because ZWJ can form a grapheme cluster
// with neighbouring chars, which trips ESLint's no-misleading-character-class.
const INVISIBLE_NAME_CHARS = /\u200B|\u200C|\u200D|\uFEFF|\u202E/u

const NAME_CONTENT_RE = /[\p{L}\p{N}\p{Extended_Pictographic}]/u

const SavedViewNameSchema = z
  .string()
  .transform((v) => v.trim())
  .pipe(
    z
      .string()
      .min(1, 'Name cannot be empty.')
      .max(
        MAX_SAVED_VIEW_NAME_LENGTH,
        `Name cannot exceed ${MAX_SAVED_VIEW_NAME_LENGTH} characters.`,
      )
      .refine((v) => !INVISIBLE_NAME_CHARS.test(v), {
        message: 'Name cannot contain invisible characters.',
      })
      .refine((v) => NAME_CONTENT_RE.test(v), {
        message: 'Name must contain at least one letter, digit, or emoji.',
      }),
  )

/**
 * Validates a saved view name. Returns the first Zod issue message on
 * failure or `null` when the name is acceptable.
 *
 * `submitting` toggles between two UX modes:
 * - typing (`submitting: false`): an empty input is "not yet filled", not an
 *   error — keep the submit button quiet until the user actually tries.
 * - submit (`submitting: true`): an empty/whitespace-only input is rejected.
 *
 * Whitespace-only names trim to `''`, which the schema rejects as empty.
 */
export function validateViewName(
  name: string,
  { submitting = false }: { submitting?: boolean } = {},
): string | null {
  if (!submitting && name.trim() === '') return null

  const result = SavedViewNameSchema.safeParse(name)
  if (result.success) return null
  return result.error.issues[0]?.message ?? 'Name is invalid'
}
